/* ENSSA Reporting — District (Level 1) and School (Level 2). */

import React from "react";
import {
  Card, Section, Btn, StatusBadge, TermToggle, StatPill, TitleWithInfo, ChartScaleTip,
} from "@/components/branded/Components";
import { RiskBreakdownStackedBar, RiskPyramid, TaskBoxPlotPanel } from "@/components/charts/Charts";
import { OverallScoreBoxPlot } from "@/components/charts/OverallScoreBoxPlot";
import { RiskTransitionSankey } from "@/components/charts/sankey";
import { TERM_DEFAULT } from "@/data/tokens";
import {
  allStudents, availableTermIds, defaultAvailableTermId, overallScoreBoxStats, riskCountsOf, statusCountsOf, taskBoxStats, getMultiStageTransitions,
} from "@/lib/report-data";
import { getPopulationReferenceBandCutoffs } from "@/lib/population-reference-cutoffs";
import { OVERALL_SCALE_NOTE, OVERALL_SCORE_DISTRIBUTION_INFO } from "@/lib/score-bands";
import type { AggregateNode, ClassRoom, District, School, YearLevel } from "@/types";
import type { PortalPopulationReferenceResponse } from "@/contracts/portal";
import { DemographicFilterBar } from "@/components/branded/DemographicFilterBar";
import { NO_DEMOGRAPHIC_FILTERS, makeDemographicPredicate, isDemographicFilterActive } from "@/lib/demographic-filters";
import type { DemographicFilters } from "@/lib/demographic-filters";

interface DistrictPageProps {
  district: District;
  yearFilter?: YearLevel | undefined;
  populationReference?: PortalPopulationReferenceResponse | null;
  onOpenSchool: (id: string) => void;
}

function DistrictPage({ district, yearFilter, populationReference = null, onOpenSchool }: DistrictPageProps) {
  const [termBands, setTermBands] = React.useState<string>(TERM_DEFAULT);
  const [termOverall, setTermOverall] = React.useState<string>(TERM_DEFAULT);
  const [termTask,  setTermTask]  = React.useState<string>(TERM_DEFAULT);
  const [demoFilters, setDemoFilters] = React.useState<DemographicFilters>(NO_DEMOGRAPHIC_FILTERS);
  const demoPredicate = React.useMemo(() => makeDemographicPredicate(demoFilters), [demoFilters]);

  const dnode: AggregateNode = { schools: district.schools };
  const filteredStudents = allStudents(dnode).filter((student) => (!yearFilter || student.year === yearFilter) && demoPredicate(student));
  const scoreTerms = availableTermIds(filteredStudents, true);
  const taskTerms = availableTermIds(filteredStudents, false);
  const resolvedTermBands = scoreTerms.includes(termBands) ? termBands : defaultAvailableTermId(scoreTerms);
  const resolvedTermOverall = scoreTerms.includes(termOverall) ? termOverall : defaultAvailableTermId(scoreTerms);
  const resolvedTermTask = taskTerms.includes(termTask) ? termTask : defaultAvailableTermId(taskTerms);
  const counts = riskCountsOf(dnode, yearFilter, demoPredicate, resolvedTermBands);
  const transitions = getMultiStageTransitions(dnode, yearFilter, demoPredicate);
  const stats = taskBoxStats(dnode, yearFilter, resolvedTermTask, demoPredicate);
  const overallScores = overallScoreBoxStats(
    filteredStudents,
    resolvedTermOverall,
  );
  const bandCutoffs = getPopulationReferenceBandCutoffs({
    populationReference,
    year: yearFilter ?? "F",
    term: resolvedTermOverall,
  });
  const status = statusCountsOf(dnode, yearFilter, demoPredicate);
  const completedPct = status.total ? Math.round(status.completed / status.total * 100) : 0;
  const totalAll = isDemographicFilterActive(demoFilters)
    ? allStudents(dnode).filter((s) => !yearFilter || s.year === yearFilter).length
    : counts.total;

  return (
    <>
      <div data-stats-row style={{ display: 'flex', gap: 48, marginBottom: 32, flexWrap: 'wrap', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 22 }}>
        <StatPill label="Schools" value={district.schools.length} />
        <StatPill label="Students" value={counts.total} />
        <StatPill label="Fully screened" value={`${completedPct}%`} />
      </div>

      <DemographicFilterBar
        value={demoFilters}
        onChange={setDemoFilters}
        totalCount={totalAll}
        filteredCount={counts.total}
      />

      <Section title="Number Sense Profile" filters={scoreTerms.length > 1 ? <TermToggle value={resolvedTermBands} onChange={setTermBands} availableTerms={scoreTerms} /> : null}>
        <Card><RiskPyramid counts={counts} /></Card>
      </Section>

      <Section
        title={<TitleWithInfo title="Overall Number Sense Score Distribution" tip={<ChartScaleTip scaleNote={OVERALL_SCALE_NOTE} bandCutoffNote={OVERALL_SCORE_DISTRIBUTION_INFO} />} />}
        filters={scoreTerms.length > 1 ? <TermToggle value={resolvedTermOverall} onChange={setTermOverall} availableTerms={scoreTerms} /> : null}
      >
        <Card><OverallScoreBoxPlot stats={overallScores} bandCutoffs={bandCutoffs} /></Card>
      </Section>

      {scoreTerms.length >= 2 && (
        <Section title="Band Changes from Term to Term">
          <Card><RiskTransitionSankey transitions={transitions} districtId={district.id} /></Card>
        </Section>
      )}

      <Section title="Subtest Performance" filters={taskTerms.length > 1 ? <TermToggle value={resolvedTermTask} onChange={setTermTask} availableTerms={taskTerms} /> : null}>
        <Card><TaskBoxPlotPanel stats={stats} /></Card>
      </Section>

      <Section title="Schools in This District">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 18 }}>
          {district.schools.map(s => {
            const sn: AggregateNode = { classes: s.classes };
            const sc = riskCountsOf(sn, yearFilter);
            const schoolStatus = statusCountsOf(sn, yearFilter);
            return (
              <Card key={s.id} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, margin: '4px 0 0' }}>{s.name}</h3>
                    <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
                      {s.leader} · {sc.total} students · last administered {s.lastAdmin}
                    </div>
                  </div>
                  <Btn variant="ghost" onClick={() => onOpenSchool(s.id)}>Open →</Btn>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--fg-3)' }}>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><StatusBadge status="completed" /><span style={{ fontFamily: 'var(--font-mono)' }}>{schoolStatus.completed}</span></span>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><StatusBadge status="partial" /><span style={{ fontFamily: 'var(--font-mono)' }}>{schoolStatus.partial}</span></span>
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}><StatusBadge status="notstarted" /><span style={{ fontFamily: 'var(--font-mono)' }}>{schoolStatus.notstarted}</span></span>
                </div>
                <RiskBreakdownStackedBar counts={sc} showLegend={false} />
              </Card>
            );
          })}
        </div>
      </Section>
    </>
  );
}

interface SchoolPageProps {
  school: School;
  district?: District | null;
  yearFilter?: YearLevel | undefined;
  onOpenClass: (id: string) => void;
}

function SchoolPage({ school, onOpenClass }: SchoolPageProps) {
  const classesBy: Record<YearLevel, ClassRoom[]> = { F: [], Y1: [] };
  for (const c of school.classes) classesBy[c.year]?.push(c);

  return (
    <>
      {classesBy.F.length > 0 && (
        <Section title="Foundation Classes">
          <ClassCardGrid classes={classesBy.F} onOpenClass={onOpenClass} />
        </Section>
      )}
      {classesBy.Y1.length > 0 && (
        <Section title="Year 1 Classes">
          <ClassCardGrid classes={classesBy.Y1} onOpenClass={onOpenClass} />
        </Section>
      )}
    </>
  );
}

interface ClassCardGridProps {
  classes: ClassRoom[];
  onOpenClass: (id: string) => void;
}

function ClassCardGrid({ classes, onOpenClass }: ClassCardGridProps) {
  return (
    <div className="class-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 18, width: '100%' }}>
      {classes.map(c => {
        const cn: AggregateNode = { students: c.students };
        const sc = riskCountsOf(cn);
        const status = statusCountsOf(cn);
        return (
          <Card key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: '4px 0 0' }}>{c.name}</h3>
                <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
                  {c.teacher} · {sc.total} students
                </div>
              </div>
              <Btn variant="ghost" onClick={() => onOpenClass(c.id)}>Open →</Btn>
            </div>
            <RiskBreakdownStackedBar counts={sc} showLegend={false} />
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--fg-3)', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><StatusBadge status="completed" /><span style={{ fontFamily: 'var(--font-mono)' }}>{status.completed}</span></span>
              <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><StatusBadge status="partial" /><span style={{ fontFamily: 'var(--font-mono)' }}>{status.partial}</span></span>
              <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><StatusBadge status="notstarted" /><span style={{ fontFamily: 'var(--font-mono)' }}>{status.notstarted}</span></span>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export { DistrictPage, SchoolPage };
